/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable react/no-danger */
import { GetStaticPaths, GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import React, { ReactElement } from 'react';

import { AuthorSocialLinks } from '@components/Author/AuthorSocialLinks';
import { BlogCardList } from '@components/Blog/BlogCardList';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Container';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { CustomImage } from '@components/Image';
import { StandardLayout } from '@components/Layouts';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { Author } from '@interfaces/Author';
import { MetaData } from '@interfaces/MetaData';
import {
  getAllAuthorSlugs,
  getAuthorData,
  getMetaData,
  getPreviewAuthorData,
} from '@lib/data/authors';
import { getRedirect } from '@lib/redirects';

const ExitPreview = dynamic(() =>
  import('../../components/Button/ExitPreview').then((mod) => mod.ExitPreview)
);

// eslint-disable-next-line no-restricted-syntax
export default function AuthorPage({
  authorData,
  meta,
  isPreview,
}: {
  authorData: Author;
  meta: MetaData;
  isPreview: boolean;
}): ReactElement {
  return (
    <>
      <Head>
        <title>{meta.metaTitle}</title>
        <meta name="title" content={meta.metaTitle} />
        <meta name="description" content={meta.metaDescription} />
        {/* <meta
          property="og:url"
          content={`https://www.bulknutrients.com.au${blogData.slug.current}`}
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blogData.title} />
        {blogData.summary !== null && (
          <meta
            property="og:description"
            content={portableTextToPlainText(blogData.summary)}
          />
        )}
        <meta
          property="og:image"
          content={urlFor(blogData.featuredImage?.asset).url() as string}
        /> */}
        {meta.noIndex && <meta name="robots" content="noindex" />}
        <link
          rel="canonical"
          href={
            meta.canonical
              ? meta.canonical
              : `${process.env.NEXT_PUBLIC_SERVER_URL}${authorData.slug}`
          }
        />
      </Head>
      {isPreview && <ExitPreview />}
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            {
              title: 'Science, Health And Nutrition Experts',
              path: '/experts',
            },
          ]}
        />
      </Container>
      <Container as="main" id="main-content" className="mb-12.5">
        <div className="my-0 max-w-narrowPage justify-self-center">
          <h1>{authorData.name}</h1>
          <CustomImage
            image={authorData.image}
            width={300}
            height={300}
            round
          />
          <AuthorSocialLinks author={authorData} />
          {authorData.description && (
            <PortableTextBlockRenderer content={authorData.description} />
          )}
          {authorData.ambassadorLink && (
            <TextLink href={authorData.ambassadorLink}>
              View more about {authorData.name}, including their recommended
              products
            </TextLink>
          )}
        </div>
        <HorizontalDivider />
        <h2 className="pb-4 italic">Bulk Blog Posts by {authorData.name}</h2>
        <BlogCardList author={authorData._id} />
      </Container>
    </>
  );
}

AuthorPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await getAllAuthorSlugs();
  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  preview,
  previewData,
}) => {
  if (preview) {
    // This is preview mode
    const authorData = await getPreviewAuthorData(
      (previewData as { id: string }).id
    );

    const meta = await getMetaData(authorData._id);

    return {
      props: {
        authorData,
        meta,
        isPreview: true,
      },
    };
  }

  const authorData = await getAuthorData(params?.author?.toString() as string);

  if (!authorData) {
    // try redirect
    const redirect = await getRedirect(
      ('/experts/' + params?.author?.toString()) as string
    );
    if (redirect) {
      return {
        redirect: {
          destination: redirect.newUrl,
          permanent: redirect.permanent,
        },
      };
    }

    // fallback failed
    return {
      notFound: true,
    };
  }

  const meta = await getMetaData(authorData._id);

  return {
    props: {
      authorData,
      meta,
      isPreview: false,
    },
  };
};
